import React from 'react';

// ─── BADGE ───────────────────────────────────────────────────────────────────
export function Badge({ type = 'gray', children, size = 'sm' }) {
  const cls = {
    blue: 'bd-blue', green: 'bd-green', amber: 'bd-amber',
    red: 'bd-red', gray: 'bd-gray'
  }[type] || 'bd-gray';
  return <span className={`bd ${cls}`} style={size === 'xs' ? { fontSize: 9 } : {}}>{children}</span>;
}

// ─── BUTTON ───────────────────────────────────────────────────────────────────
export function Btn({ variant = 'default', size = 'default', children, onClick, disabled, type = 'button', className = '' }) {
  const variants = {
    default: 'btn',
    primary: 'btn btn-p',
    success: 'btn btn-success',
    ghost: 'btn border-transparent hover:bg-transparent text-gray-500',
  };
  const sizes = { sm: 'btn-s', default: '', lg: 'px-4 py-2 text-sm' };
  return (
    <button
      type={type} onClick={onClick} disabled={disabled}
      className={`${variants[variant] || 'btn'} ${sizes[size]} ${className} ${disabled ? 'opacity-40 pointer-events-none' : ''}`}
    >
      {children}
    </button>
  );
}

// ─── KPI CARD ─────────────────────────────────────────────────────────────────
export function KpiCard({ label, value, sub, type = 'default', progress }) {
  const types = {
    default: { bg: 'var(--bg-secondary)', color: 'var(--text-primary)', labelColor: 'var(--text-secondary)' },
    blue:    { bg: 'var(--acc-bg)',  color: 'var(--acc)',   labelColor: 'var(--acc-t)' },
    green:   { bg: 'var(--grn-bg)', color: 'var(--grn)',   labelColor: 'var(--grn)' },
    amber:   { bg: 'var(--amb-bg)', color: 'var(--amb)',   labelColor: 'var(--amb)' },
    red:     { bg: 'var(--red-bg)', color: 'var(--red)',   labelColor: 'var(--red)' },
  };
  const t = types[type] || types.default;
  return (
    <div className="kpi" style={{ background: t.bg }}>
      <div className="kpi-label" style={{ color: t.labelColor }}>{label}</div>
      <div className="kpi-val" style={{ color: t.color }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: t.labelColor, marginTop: 3, opacity: 0.85 }}>{sub}</div>}
      {progress !== undefined && (
        <div className="prog-bg">
          <div className="prog-fill" style={{ width: `${Math.min(progress, 100)}%`, background: t.color }} />
        </div>
      )}
    </div>
  );
}

// ─── TOGGLE ───────────────────────────────────────────────────────────────────
export function Toggle({ value, onChange }) {
  return (
    <div className={`toggle ${value ? 'on' : ''}`} onClick={() => onChange(!value)}>
      <div className="toggle-knob" />
    </div>
  );
}

// ─── INPUT ────────────────────────────────────────────────────────────────────
export function Input({ value, onChange, placeholder, type = 'text', style, className = '' }) {
  return (
    <input
      className={`inp ${className}`} type={type} value={value || ''} placeholder={placeholder}
      onChange={e => onChange(e.target.value)} style={style}
    />
  );
}

// ─── SELECT ───────────────────────────────────────────────────────────────────
export function Select({ value, onChange, options = [], placeholder, style }) {
  return (
    <select className="inp" value={value || ''} onChange={e => onChange(e.target.value)} style={style}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 400 }) {
  if (!open) return null;
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.28)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--bg-primary)', border: '0.5px solid var(--border-md)', borderRadius: 12, padding: 20, width, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto' }}>
        {title && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 13.5, fontWeight: 500 }}>{title}</div>
            <button onClick={onClose} className="btn btn-s" style={{ borderColor: 'transparent', color: 'var(--text-secondary)' }}>✕</button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

// ─── EMPTY STATE ──────────────────────────────────────────────────────────────
export function EmptyState({ message = 'No data', action }) {
  return (
    <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
      <div style={{ fontSize: 13 }}>{message}</div>
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

// ─── LOADING ──────────────────────────────────────────────────────────────────
export function Loading({ text = 'Loading…' }) {
  return (
    <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
      {text}
    </div>
  );
}

// ─── SECTION LABEL ────────────────────────────────────────────────────────────
export function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 8 }}>
      {children}
    </div>
  );
}

// ─── DIVIDER ──────────────────────────────────────────────────────────────────
export function Divider() {
  return <div style={{ height: '0.5px', background: 'var(--border)', margin: '10px 0' }} />;
}
